import type React from 'react';
import { type SetStateAction, createContext, useEffect, useState, useMemo, useRef, useCallback, type JSX } from 'react';
import Script from 'next/script';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { pianoInit, defaultSubscriberContext, pianoMyAccount } from 'services/Piano';
import { Subscriber, validatePianoCookie } from 'services/Subscriber';
import { useRouter } from 'next/router';
import { useOptimizely, type OptimizelyUserInfo } from 'providers/OptimizelyProvider';
import { subscriberPush, trackEvent, userPush } from 'services/Gtm/functions';
import type { BypassPaywall } from 'interfaces/BypassPaywall';
import type CustomWindow from 'interfaces/Utils';
import { changeRoute } from 'components/Globals/Base/Link';

import type { ConversionData, ResponseVariables, SetterMap } from 'services/Piano/entities/PianoCallbackParams';
import ConversionTracker from 'components/ConversionTracker';
import captureSFEmailParams from 'utils/captureSFEmailParams';
import findOnetrustCookieOptIn, {
  findOnetrustCookieOptInAds,
  findOnetrustCookiePerformance,
} from 'utils/miscUtils/CheckConsentCookies';
import type { EventData } from 'services/Gtm/entities';

declare let window: CustomWindow;
interface UserProviderProps {
  children: JSX.Element[];
  pianoPageConfig: PianoPageConfig;
  title: string;
  databaseId: string;
  bypassPaywall?: BypassPaywall;
}
interface UserContextInterface {
  hasLoggedIn: boolean;
  isBypassed: boolean;
  userVariants: OptimizelyUserInfo;
  paywall30: string;
  setUserState: React.Dispatch<SetStateAction<typeof defaultSubscriberContext>>;
  userState: typeof defaultSubscriberContext;
  hideInStreamAds: boolean;
  hideOutbrain: boolean;
  stopVideoDock: boolean;
  hasConsent: boolean | null;
  performanceTracking: boolean | null;
  personalizedAds: boolean | null;
  pianoScriptFailed: boolean;
  setHasConsent: React.Dispatch<SetStateAction<boolean | null>>;
  setPersonalizedAds: React.Dispatch<SetStateAction<boolean | null>>;
  setPerformanceTracking: React.Dispatch<SetStateAction<boolean | null>>;
  showPaywallax: boolean;
  isMobileApp: boolean;
}

const defaultSetUserState = (() => {}) as React.Dispatch<SetStateAction<typeof defaultSubscriberContext>>;
const defaultSetHasConsent = (() => {}) as React.Dispatch<SetStateAction<boolean | null>>;

export const UserContext = createContext({
  hasConsent: null,
  hasLoggedIn: false,
  hideInStreamAds: false,
  hideOutbrain: false,
  isBypassed: false,
  paywall30: 'pending',
  performanceTracking: null,
  personalizedAds: null,
  pianoScriptFailed: false,
  setHasConsent: defaultSetHasConsent,
  setPerformanceTracking: defaultSetHasConsent,
  setPersonalizedAds: defaultSetHasConsent,
  setUserState: defaultSetUserState,
  stopVideoDock: false,
  userState: defaultSubscriberContext,
  userVariants: {},
  showPaywallax: false,
  isMobileApp: false,
}) as React.Context<UserContextInterface>;

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  pianoPageConfig,
  bypassPaywall,
  title,
  databaseId,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [initialized, setInitalized] = useState(false);
  const [hasLoggedIn, setHasLoggedIn] = useState(false);
  const [isBypassed, setIsBypassed] = useState(false);
  const [userState, setUserState] = useState(defaultSubscriberContext);
  const [paywall30, setPaywall30] = useState('pending');
  const [hideOutbrain, setHideOutbrain] = useState(false);
  const [hideInStreamAds, setHideInStreamAds] = useState(false);
  const [stopVideoDock, setStopVideoDock] = useState(false);
  const [pianoScriptFailed, setPianoScriptFailed] = useState(false);
  const [conversionData, setConversionData] = useState<ConversionData | null>(null);
  const router = useRouter();
  const allowBypass = useRef(false);
  const [hasConsent, setHasConsent] = useState<boolean | null>(!!findOnetrustCookieOptIn());
  const { userFlags: userVariants } = useOptimizely();
  const [personalizedAds, setPersonalizedAds] = useState<boolean | null>(!!findOnetrustCookieOptInAds());
  const [performanceTracking, setPerformanceTracking] = useState<boolean | null>(!!findOnetrustCookiePerformance());
  const allowTracking = useRef(false);
  const eventQueueRef = useRef<any[]>([]);
  const [showPaywallax, setShowPaywallax] = useState(false);
  const [isMobileApp, setIsMobileApp] = useState(false);

  const setterMap: SetterMap = useMemo(
    () => ({
      hideInStreamAds: (val) => setHideInStreamAds(val),
      hideOutbrain: (val) => setHideOutbrain(val),
      paywall30: (val) => setPaywall30(val),
      stopVideoDock: (val) => setStopVideoDock(val),
      showPaywallax: (val) => setShowPaywallax(val),
    }),
    [],
  );

  const updateResponseVariables = useCallback(
    (responseVariables: ResponseVariables) => {
      (
        Object.entries(responseVariables) as [keyof ResponseVariables, ResponseVariables[keyof ResponseVariables]][]
      ).forEach(([key, value]) => {
        if (key in setterMap) {
          (setterMap[key] as React.Dispatch<React.SetStateAction<typeof value>>)(value);
        }
      });
    },
    [setterMap],
  );

  useEffect(() => {
    if (userState.isLoggedIn === false && router.pathname.includes('my-account')) {
      if (router.pathname.includes('my-account/newsletters')) {
        void changeRoute('/newsletters/');
      } else {
        void changeRoute('/');
      }
    }
  }, [userState.isLoggedIn, router.pathname]);

  useEffect(() => {
    setHasLoggedIn(validatePianoCookie());
  }, []);

  useEffect(() => {
    if (router.isReady && process.env.FEATURE_FLAG_SF_CONVERSION_TRACKING) {
      captureSFEmailParams(router.query);
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    if (bypassPaywall && bypassPaywall.postBypassLinkEnabled) {
      allowBypass.current = Object.keys(router.query).some((query) => query === bypassPaywall.postBypassLinkUnique);
    }
    setIsBypassed(allowBypass.current);
  }, [router.isReady, router.query, bypassPaywall]);

  // Remove paywall anchor element in case experience executes before router params are ready
  useEffect(() => {
    if (isBypassed && paywall30 === 'paywalled') {
      const paywallAnchor = document.getElementsByClassName('paywall-selector')[0];
      paywallAnchor?.remove();
    }
  }, [isBypassed, paywall30]);

  useEffect(() => {
    try {
      if (performanceTracking) {
        allowTracking.current = performanceTracking;
        // No longer using segment but may migrate to another analytics provider in the future
        /* const { analytics } = window;
        const sessionIdentifiers = getSessionIdentifiers();

        eventQueueRef.current.forEach((event: PianoHandlerEvent) => {
          const { eventName, eventData, eventType } = event;
          if (eventType === 'track') {
            const eventPayload = {
              ...sessionIdentifiers,
              ...eventData,
            };
            if (analytics) {
              analytics.track(eventName, eventPayload);
            }
          } else if (eventType === 'identify') {
            analytics.identify(eventData);
          }
        }); */

        eventQueueRef.current = [];
      }
    } catch (error) {
      console.error('Error processing queued paywall funnel events:', error);
    }
  }, [performanceTracking]);

  const sendEventOnConsent = useCallback((event: EventData) => {
    try {
      if (allowTracking.current) {
        trackEvent(event);
      } else {
        eventQueueRef.current.push(event);
      }
    } catch (error) {
      console.error('Error processing paywall funnel event:', error);
    }
  }, []);

  useEffect(() => {
    if (loaded) {
      pianoInit(pianoPageConfig, isBypassed, updateResponseVariables, sendEventOnConsent, setConversionData);
    }
  }, [isBypassed, loaded, pianoPageConfig, sendEventOnConsent, updateResponseVariables, setConversionData]);

  useEffect(() => {
    if (loaded && !initialized) {
      const initSubscriber = async () => {
        const subscriber = new Subscriber();
        await subscriber.init();

        return subscriber;
      };
      initSubscriber()
        .then((subscriber) => {
          subscriberPush(subscriber);
          userPush(!!subscriber.isLoggedIn);
          setUserState(subscriber);
          setInitalized(true);
        })
        .catch((error) => console.error(error));
      if (router.pathname.includes('/my-account/payment')) pianoMyAccount();
    }
  }, [initialized, loaded, router.pathname]);

  useEffect(() => {
    const userAgentIndicatesMobileApp =
      navigator.userAgent.includes('iPhone') &&
      !navigator.userAgent.includes('Version/') &&
      !navigator.userAgent.includes('Safari/');
    if (userAgentIndicatesMobileApp) {
      setIsMobileApp(true);
    }
  }, []);

  const memoizedUserState = useMemo(
    () => ({
      hasConsent,
      hasLoggedIn,
      hideInStreamAds,
      hideOutbrain,
      isBypassed,
      paywall30,
      performanceTracking,
      personalizedAds,
      pianoScriptFailed,
      setHasConsent,
      setPerformanceTracking,
      setPersonalizedAds,
      setUserState,
      stopVideoDock,
      userState,
      userVariants,
      showPaywallax,
      isMobileApp,
    }),
    [
      hasLoggedIn,
      hideInStreamAds,
      hideOutbrain,
      stopVideoDock,
      isBypassed,
      paywall30,
      pianoScriptFailed,
      userState,
      setUserState,
      userVariants,
      hasConsent,
      performanceTracking,
      personalizedAds,
      setHasConsent,
      setPersonalizedAds,
      setPerformanceTracking,
      showPaywallax,
      isMobileApp,
    ],
  );

  return (
    <>
      <Script
        src={process.env.PIANO_CDN_URL}
        id='piano-id'
        strategy='afterInteractive'
        onLoad={() => setLoaded(true)}
        onError={() => {
          console.error('Failed to load piano script');
          setPianoScriptFailed(true);
          setPaywall30('no-paywall');
        }}
      />
      <UserContext.Provider value={memoizedUserState}>{children}</UserContext.Provider>
      {conversionData && process.env.FEATURE_FLAG_SF_CONVERSION_TRACKING && (
        <ConversionTracker
          conversionData={conversionData}
          databaseId={databaseId}
          title={title}
        />
      )}
    </>
  );
};
